import { useParams, Link, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { format } from 'date-fns';
import { ArrowLeft, Tag } from 'lucide-react';
import BlogContent from '../components/blog/BlogContent';
import BlogCard from '../components/blog/BlogCard';
import blogPostsData from '../data/blog-posts.json';
import type { BlogPost } from '../types/blog';

const blogPosts = blogPostsData as BlogPost[];

// Disqus configuration
const DISQUS_SHORTNAME = 'creativeyadley';

export default function BlogPostPage() {
    const { slug } = useParams<{ slug: string }>();

    // Find the current post
    const post = blogPosts.find(p => p.slug === slug);

    // If post not found, redirect to blog page
    if (!post) {
        return <Navigate to="/blog" replace />;
    }

    // Find related posts (same category, excluding current post)
    const relatedPosts = blogPosts
        .filter(p =>
            p.id !== post.id &&
            p.categories.some(cat => post.categories.includes(cat))
        )
        .slice(0, 3);

    // Load Disqus comments
    useEffect(() => {
        // Reset Disqus if it already exists
        if (window.DISQUS) {
            window.DISQUS.reset({
                reload: true,
                config: function () {
                    this.page.url = window.location.href;
                    this.page.identifier = slug;
                    this.page.title = post.title;
                }
            });
        } else {
            // Load Disqus for the first time
            window.disqus_config = function () {
                this.page.url = window.location.href;
                this.page.identifier = slug;
                this.page.title = post.title;
            };

            const script = document.createElement('script');
            script.src = `https://${DISQUS_SHORTNAME}.disqus.com/embed.js`;
            script.setAttribute('data-timestamp', String(+new Date()));
            (document.head || document.body).appendChild(script);
        }
    }, [slug, post.title]);

    return (
        <div className="min-h-screen bg-white pt-24 text-gray-900">
            {/* Back Button */}
            <div className="w-[85vw] mx-auto mb-8">
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-gray-600 hover:text-[#d68800] transition-colors"
                    style={{ fontFamily: 'Sofia Pro, sans-serif' }}
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Blog
                </Link>
            </div>

            {/* Article */}
            <article className="w-[85vw] mx-auto pb-24">
                {/* Header */}
                <header className="mb-12">
                    {/* Categories */}
                    {post.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6">
                            {post.categories.map((category, index) => (
                                <span
                                    key={index}
                                    className="text-xs px-4 py-2 bg-gray-100 text-gray-700 font-medium uppercase tracking-wider"
                                    style={{ fontFamily: 'Sofia Pro, sans-serif' }}
                                >
                                    {category}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Title */}
                    <h1
                        className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-[#293039] leading-tight"
                        style={{ fontFamily: 'Sofia Pro, sans-serif' }}
                    >
                        {post.title}
                    </h1>

                    {/* Meta Info - Smaller font size with separators */}
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mb-8">
                        <span style={{ fontFamily: 'Sofia Pro, sans-serif' }}>{post.author}</span>
                        <span>·</span>
                        <span style={{ fontFamily: 'Sofia Pro, sans-serif' }}>
                            {format(new Date(post.publishedDate), 'MMMM dd, yyyy')}
                        </span>
                        <span>·</span>
                        <span style={{ fontFamily: 'Sofia Pro, sans-serif' }}>{post.readTime} min read</span>
                    </div>

                    {/* Featured Image */}
                    {post.featuredImage && (
                        <div className="mb-12">
                            <img
                                src={post.featuredImage}
                                alt={post.title}
                                className="w-full h-auto max-h-[600px] object-cover shadow-lg rounded-2xl"
                            />
                        </div>
                    )}
                </header>

                {/* Content - applied improved typography classes */}
                <div className="max-w-4xl mx-auto mt-8 space-y-6 text-neutral-700 leading-relaxed text-lg">
                    <BlogContent content={post.content} />

                    {/* Tags */}
                    {post.tags.length > 0 && (
                        <div className="mt-12 pt-8 border-t border-gray-200">
                            <div className="flex items-start gap-3">
                                <Tag className="w-5 h-5 text-gray-600 mt-1" />
                                <div className="flex flex-wrap gap-2">
                                    {post.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="text-sm px-3 py-1 bg-gray-100 text-gray-700"
                                            style={{ fontFamily: 'Sofia Pro, sans-serif' }}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Disqus Comments */}
                <div className="max-w-4xl mx-auto mt-16 pt-12 border-t border-gray-200">
                    <h2
                        className="text-3xl font-bold mb-8 text-gray-900"
                        style={{ fontFamily: 'Sofia Pro, sans-serif' }}
                    >
                        Comments
                    </h2>
                    <div id="disqus_thread"></div>
                    <noscript>
                        Please enable JavaScript to view the{' '}
                        <a href="https://disqus.com/?ref_noscript">comments powered by Disqus.</a>
                    </noscript>
                </div>

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                    <div className="related-posts mt-20 pt-12 border-t border-gray-200">
                        <h2
                            className="text-3xl font-bold mb-8 text-gray-900"
                            style={{ fontFamily: 'Sofia Pro, sans-serif' }}
                        >
                            Related Posts
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {relatedPosts.map(relatedPost => (
                                <BlogCard key={relatedPost.id} {...relatedPost} />
                            ))}
                        </div>
                    </div>
                )}
            </article>
        </div>
    );
}

// TypeScript declarations for Disqus
declare global {
    interface Window {
        DISQUS: any;
        disqus_config: any;
    }
}
